
import React, { useState, useEffect, useRef, useCallback } from 'react';
import DOMPurify from 'dompurify';
import axios from 'axios';
import {
    Box, Container, Typography, Paper, TextField, Button,
    Card, CardContent, Avatar, Chip, IconButton, InputAdornment, Divider,
    CircularProgress, Alert, Snackbar, Tooltip, Stack
} from '@mui/material';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VerifiedIcon from '@mui/icons-material/Verified';
import ForumIcon from '@mui/icons-material/Forum';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import PendingIcon from '@mui/icons-material/Pending';
import BadgeIcon from '@mui/icons-material/Badge';
import VisibilityIcon from '@mui/icons-material/Visibility';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const theme = createTheme({
    palette: {
        primary: { main: '#11224d' },
        secondary: { main: '#0e1d3a' },
        warning: { main: '#ffb300' },
        background: { default: '#f8f9fa', paper: '#ffffff' },
        text: { primary: '#202124', secondary: '#5f6368' }
    },
    typography: { fontFamily: 'Inter, Roboto, Arial, sans-serif' },
    components: {
        MuiButton: {
            styleOverrides: {
                root: { textTransform: 'none', fontWeight: 500, borderRadius: 4 },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: { borderRadius: 8 },
            },
        },
    },
});

const HeroSection = styled(Box)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    color: 'white',
    padding: theme.spacing(1.5, 2),
    textAlign: 'center',
    marginBottom: theme.spacing(2),
}));

const SearchBar = styled(TextField)(({ theme }) => ({
    backgroundColor: 'white',
    borderRadius: 14,
    '& .MuiOutlinedInput-root': {
        borderRadius: 24,
        padding: '2px 16px',
        '& fieldset': { border: 'none' },
        '& input': {
            padding: '8px 8px',
            fontSize: '0.95rem',
        },
    },
    maxWidth: 700,
    width: '100%',
    boxShadow: '0 1px 6px rgba(32,33,36,.28)',
}));

const PostCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    border: '1px solid #dadce0',
    boxShadow: 'none',
    transition: 'all 0.2s',
    '&:hover': {
        boxShadow: '0 1px 2px 0 rgba(60,64,67,.3), 0 1px 3px 1px rgba(60,64,67,.15)',
        borderColor: '#d2d2d2'
    }
}));

const ReplyCard = styled(Paper)(({ theme, isadmin, ispending, ishighlighted }) => ({
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: ispending === 'true' 
        ? '#fff3e0' 
        : isadmin === 'true' 
            ? '#f8f9ff' 
            : '#ffffff',
    border: `2px solid ${
        ishighlighted === 'true'
            ? theme.palette.warning.main
            : ispending === 'true'
                ? '#ff9800'
                : isadmin === 'true' 
                    ? theme.palette.primary.light 
                    : '#e0e0e0'
    }`,
    boxShadow: ishighlighted === 'true' ? '0 4px 12px rgba(255, 152, 0, 0.3)' : 'none',
    transition: 'all 0.3s ease',
}));

const FormatToolbar = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px 8px 0 0',
}));

const StyledBackButton = styled(Button)(({ theme }) => ({
  padding: '5px 10px',
  borderRadius: '7px',
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  borderColor: theme.palette.warning.main,
  backgroundColor: theme.palette.warning.main,
  textTransform: 'uppercase',
  fontSize: '0.65rem',
  marginBottom: theme.spacing(2),
  '&:hover': {
    backgroundColor: '#e69900',
    borderColor: '#e69900',
  },
}));

const renderFormattedText = text => {
    if (!text) return { __html: '' };
    
    let formatted = text;

    if (!/<[a-z][\s\S]*>/i.test(text)) {
        const escapedText = text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
        formatted = escapedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        formatted = formatted.replace(/(?<!\*)\*(?!\*)([^*]+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
        formatted = formatted.replace(/(^|[^*])\*([^*]+?)\*(?=[^*]|$)/g, '$1<em>$2</em>');
        formatted = formatted.replace(/__(.*?)__/g, '<u>$1</u>');
        formatted = formatted.replace(/!\[image\]\((.*?)\)/g, '<img src="$1" style="max-width: 100%; border-radius: 8px; margin: 8px 0;" alt="uploaded" />');
        formatted = formatted.replace(/^• /gm, '• ');
        formatted = formatted.replace(/\n/g, '<br/>');
    }

    return { __html: DOMPurify.sanitize(formatted) };
};

const RichTextEditor = ({ value, onChange, placeholder, minRows = 4, onAttach }) => {
    const editorRef = useRef(null);
    const [activeFormats, setActiveFormats] = useState({
        bold: false,
        italic: false,
        underline: false,
        insertUnorderedList: false,
        insertOrderedList: false,
    });

    useEffect(() => {
        if (editorRef.current && document.activeElement !== editorRef.current) {
             const html = renderFormattedText(value).__html;
             if (editorRef.current.innerHTML !== html) {
                 editorRef.current.innerHTML = html;
             }
        }
    }, [value]);

    const updateActiveFormats = React.useCallback(() => {
        setActiveFormats({
            bold: document.queryCommandState('bold'),
            italic: document.queryCommandState('italic'),
            underline: document.queryCommandState('underline'),
            insertUnorderedList: document.queryCommandState('insertUnorderedList'),
            insertOrderedList: document.queryCommandState('insertOrderedList'),
        });
    }, []);

    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) return;

        const handleSelectionChange = () => {
            if (document.activeElement === editor) {
                updateActiveFormats();
            }
        };

        document.addEventListener('selectionchange', handleSelectionChange);
        editor.addEventListener('click', updateActiveFormats);
        editor.addEventListener('keyup', updateActiveFormats);
        editor.addEventListener('focus', updateActiveFormats);

        return () => {
            document.removeEventListener('selectionchange', handleSelectionChange);
        };
    }, [editorRef, updateActiveFormats]);

    const handleInput = (e) => {
        onChange({ target: { value: e.currentTarget.innerHTML } });
    };

    const applyFormat = (command) => {
        document.execCommand(command, false, null);
        editorRef.current.focus();
    };

    const handleKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
            if (e.key === 'b') {
                e.preventDefault();
                applyFormat('bold');
            } else if (e.key === 'i') {
                e.preventDefault();
                applyFormat('italic');
            } else if (e.key === 'u') {
                e.preventDefault();
                applyFormat('underline');
            }
        }
    };

    const getButtonSx = (isActive) => ({
        color: isActive ? 'text.primary' : 'text.secondary',
        bgcolor: isActive ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
        '&:hover': {
            bgcolor: isActive ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.04)',
        }
    });

    return (
        <Box sx={{ border: '1px solid #dadce0', borderRadius: 2, overflow: 'hidden', bgcolor: 'white' }}>
            <FormatToolbar>
                <Tooltip title="Bold (Ctrl+B)">
                    <IconButton size="small" onClick={() => applyFormat('bold')} sx={getButtonSx(activeFormats.bold)}>
                        <FormatBoldIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Italic (Ctrl+I)">
                    <IconButton size="small" onClick={() => applyFormat('italic')} sx={getButtonSx(activeFormats.italic)}>
                        <FormatItalicIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Underline (Ctrl+U)">
                    <IconButton size="small" onClick={() => applyFormat('underline')} sx={getButtonSx(activeFormats.underline)}>
                        <FormatUnderlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Box sx={{ width: '1px', bgcolor: '#e0e0e0', mx: 0.5 }} />
                <Tooltip title="Bullet List">
                    <IconButton size="small" onClick={() => applyFormat('insertUnorderedList')} sx={getButtonSx(activeFormats.insertUnorderedList)}>
                        <FormatListBulletedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Numbered List">
                    <IconButton size="small" onClick={() => applyFormat('insertOrderedList')} sx={getButtonSx(activeFormats.insertOrderedList)}>
                        <FormatListNumberedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                {onAttach && (
                    <Tooltip title="Attach Images">
                        <IconButton size="small" component="label">
                            <CloudUploadIcon fontSize="small" />
                            <input type="file" hidden multiple accept="image/*" onChange={onAttach} />
                        </IconButton>
                    </Tooltip>
                )}
            </FormatToolbar>
            <Box
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                sx={{
                    minHeight: minRows * 24,
                    p: 2,
                    outline: 'none',
                    fontFamily: 'inherit',
                    fontSize: '1rem',
                    color: 'text.primary',
                    '&:empty:before': {
                        content: `"${placeholder}"`,
                        color: 'text.secondary',
                        cursor: 'text',
                        display: 'block'
                    },
                    '& ul, & ol': {
                        pl: 2,
                        mt: 0.5,
                        mb: 0.5,
                    },
                }}
            />
        </Box>
    );
};

const SupportForum = ({ currentUser, initialView = 'list', onNavigate }) => {
    const [view, setView] = useState(initialView);
    const [posts, setPosts] = useState([]);
    const [currentPost, setCurrentPost] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [newPostTitle, setNewPostTitle] = useState('');
    const [newPostContent, setNewPostContent] = useState('');
    const [replyContent, setReplyContent] = useState('');
    const [pendingReplies, setPendingReplies] = useState([]);
    const [pendingPosts, setPendingPosts] = useState([]);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null, type: null, action: null });
    const [showMyPosts, setShowMyPosts] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [editContent, setEditContent] = useState('');
    const [customDisplayName, setCustomDisplayName] = useState('');
    const [replyDisplayName, setReplyDisplayName] = useState('');
    const [editDisplayName, setEditDisplayName] = useState('');
    const [highlightReplyId, setHighlightReplyId] = useState(null);

    const [newPostImages, setNewPostImages] = useState([]);
    const [newPostPreviews, setNewPostPreviews] = useState([]);
    const [replyImages, setReplyImages] = useState([]);
    const [replyPreviews, setReplyPreviews] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const highlightedReplyRef = useRef(null);

    const isAdmin = currentUser?.role?.toLowerCase() === 'admin';

    useEffect(() => { setView(initialView); }, [initialView]);

    useEffect(() => {
        if (highlightReplyId && highlightedReplyRef.current) {
            setTimeout(() => {
                highlightedReplyRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center' 
                });
            }, 300);
        }
    }, [highlightReplyId, currentPost]);

    const fetchPosts = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('jwtToken');
            const url = `${API_BASE_URL}/api/support/posts?search=${searchQuery}${showMyPosts ? '&mine=true' : ''}`;
            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPosts(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [searchQuery, showMyPosts]);

    const fetchPostDetails = async (postId, replyIdToHighlight = null) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('jwtToken');
            let url = `${API_BASE_URL}/api/support/posts/${postId}`;
            
            if (replyIdToHighlight && isAdmin) {
                 url = `${API_BASE_URL}/api/support/admin/posts/${postId}/reply/${replyIdToHighlight}`;
            }

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCurrentPost(res.data);
            setHighlightReplyId(replyIdToHighlight);
            setView('detail');
        } catch (err) {
            console.error(err);
            showNotification('Failed to load post details', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchPendingContent = useCallback(async () => {
        try {
            const token = localStorage.getItem('jwtToken');
            const [repliesRes, postsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/support/admin/pending`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_BASE_URL}/api/support/admin/posts/pending`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setPendingReplies(repliesRes.data);
            setPendingPosts(postsRes.data);
        } catch (err) {
            console.error(err);
        }
    }, []);

    useEffect(() => {
        if (view === 'list') fetchPosts();
        if (view === 'moderation' && isAdmin) fetchPendingContent();
    }, [view, isAdmin, fetchPosts, fetchPendingContent]);

    const handleFileSelect = (e, setFiles, setPreviews) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            setFiles(prev => [...prev, ...files]);
            const newPreviews = files.map(file => ({
                url: URL.createObjectURL(file),
                name: file.name
            }));
            setPreviews(prev => [...prev, ...newPreviews]);
        }
        e.target.value = null;
    };

    const handleRemovePreview = (index, setFiles, setPreviews) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
        setPreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleCreatePost = async () => {
        if (!newPostTitle.trim() || !newPostContent.trim()) return;
        
        if (isSubmitting) return;
        setIsSubmitting(true);

        const formData = new FormData();
        formData.append('title', newPostTitle);
        formData.append('content', newPostContent);
        if (isAdmin && customDisplayName.trim()) {
            formData.append('customDisplayName', customDisplayName);
        }
        newPostImages.forEach(file => {
            formData.append('images', file);
        });

        try {
            const token = localStorage.getItem('jwtToken');
            const response = await axios.post(`${API_BASE_URL}/api/support/posts`, 
                formData,
                { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
            );
            setNewPostTitle('');
            setNewPostContent('');
            setCustomDisplayName('');
            setNewPostImages([]);
            setNewPostPreviews([]);
            setView('list');
            showNotification(response.data.message || 'Question submitted for approval. It will appear once approved by an admin.');
        } catch (err) {
            showNotification('Failed to post question', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReply = async () => {
        if (!replyContent.trim()) return;
        
        if (isSubmitting) return;
        setIsSubmitting(true);

        const formData = new FormData();
        formData.append('content', replyContent);
        if (isAdmin && replyDisplayName.trim()) {
            formData.append('customDisplayName', replyDisplayName);
        }
        replyImages.forEach(file => {
            formData.append('images', file);
        });

        try {
            const token = localStorage.getItem('jwtToken');
            const res = await axios.post(`${API_BASE_URL}/api/support/posts/${currentPost.post.id}/replies`,
                formData,
                { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
            );
            setReplyDisplayName('');
            setReplyContent('');
            setReplyImages([]);
            setReplyPreviews([]);
            showNotification(res.data.message);
            fetchPostDetails(currentPost.post.id, highlightReplyId);
        } catch (err) {
            showNotification('Failed to submit reply', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleModeration = async (id, type, action) => {
        try {
            const token = localStorage.getItem('jwtToken');
            const endpoint = type === 'post' ? 'posts' : 'replies';
            if (action === 'approve') {
                await axios.put(`${API_BASE_URL}/api/support/admin/${endpoint}/${id}/approve`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                showNotification(`${type === 'post' ? 'Post' : 'Reply'} approved`);
            } else {
                await axios.delete(`${API_BASE_URL}/api/support/admin/${endpoint}/${id}/reject`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                showNotification(`${type === 'post' ? 'Post' : 'Reply'} rejected`);
            }
            if (view === 'detail' && currentPost) {
                fetchPostDetails(currentPost.post.id, highlightReplyId);
            }
            
            fetchPendingContent();
        } catch (err) {
            showNotification('Action failed', 'error');
        }
        setConfirmDialog({ open: false, id: null, type: null, action: null });
    };

    const handleViewPendingReply = async (reply) => {
        await fetchPostDetails(reply.post_id, reply.id);
    };

    const handleEditClick = () => {
        setEditTitle(currentPost.post.title);
        setEditDisplayName('');
        setEditContent(currentPost.post.content);
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditDisplayName('');
        setEditTitle('');
        setEditContent('');
    };

    const handleUpdatePost = async () => {
        if (!editTitle.trim() || !editContent.trim()) return;
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const token = localStorage.getItem('jwtToken');
            const updateData = { title: editTitle, content: editContent };
            if (isAdmin && editDisplayName.trim()) {
                updateData.customDisplayName = editDisplayName;
            }
            const response = await axios.put(`${API_BASE_URL}/api/support/posts/${currentPost.post.id}`, 
                updateData,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            showNotification(response.data.message || 'Post updated and submitted for approval.');
            setIsEditing(false);
            fetchPostDetails(currentPost.post.id);
        } catch (err) {
            showNotification('Failed to update post', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeletePost = () => {
        setConfirmDialog({
            open: true,
            title: 'Delete Post',
            message: 'Are you sure you want to delete this post? This cannot be undone.',
            onConfirm: async () => {
                try {
                    const token = localStorage.getItem('jwtToken');
                    // allow admins to delete via admin endpoint
                    const endpoint = isAdmin ? `${API_BASE_URL}/api/support/admin/posts/${currentPost.post.id}` : `${API_BASE_URL}/api/support/posts/${currentPost.post.id}`;
                    await axios.delete(endpoint, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    showNotification('Post deleted successfully');
                    setView('list');
                    setConfirmDialog({ ...confirmDialog, open: false });
                } catch (err) {
                    showNotification('Failed to delete post', 'error');
                    setConfirmDialog({ ...confirmDialog, open: false });
                }
            }
        });
    };

    const handleAdminDeletePost = (postId) => {
        setConfirmDialog({
            open: true,
            title: 'Delete Post',
            message: 'Are you sure you want to delete this post? This cannot be undone.',
            onConfirm: async () => {
                try {
                    const token = localStorage.getItem('jwtToken');
                    await axios.delete(`${API_BASE_URL}/api/support/admin/posts/${postId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    showNotification('Post deleted successfully');
                    // refresh list or close detail if currently viewing
                    if (view === 'detail' && currentPost && currentPost.post.id === postId) {
                        setView('list');
                    }
                    fetchPosts();
                } catch (err) {
                    console.error(err);
                    showNotification('Failed to delete post', 'error');
                }
                setConfirmDialog({ open: false, id: null, type: null, action: null });
            }
        });
    };

    const handleAdminDeleteReply = (replyId) => {
        setConfirmDialog({
            open: true,
            title: 'Delete Reply',
            message: 'Are you sure you want to delete this reply? This cannot be undone.',
            onConfirm: async () => {
                try {
                    const token = localStorage.getItem('jwtToken');
                    await axios.delete(`${API_BASE_URL}/api/support/admin/replies/${replyId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    showNotification('Reply deleted successfully');
                    if (view === 'detail' && currentPost) fetchPostDetails(currentPost.post.id);
                } catch (err) {
                    console.error(err);
                    showNotification('Failed to delete reply', 'error');
                }
                setConfirmDialog({ open: false, id: null, type: null, action: null });
            }
        });
    };

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    const renderHeader = () => (
        <HeroSection>
            <Container maxWidth="md">
                <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 1.5 }}>
                    How can we help you?
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.95, mb: 2, fontSize: '0.95rem', fontWeight: 'bold' }}>
                    Search our community discussions or ask a question
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <SearchBar
                        placeholder="Describe your issue"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && fetchPosts()}
                        InputProps={{
                            startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>),
                        }}
                    />
                </Box>
            </Container>
        </HeroSection>
    );

    const renderListView = () => (
        <Box>
            {renderHeader()}
            <Container maxWidth="md">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 2 }}>
                    <Typography variant="h5" fontWeight="bold" color="primary">Community Discussions</Typography>
                    <Stack direction="row" spacing={1}>
                        {onNavigate && (
                            <Button variant="outlined" size="medium" startIcon={<HelpOutlineIcon />} onClick={() => onNavigate('Help Center', 'Community Forum')}>Help Center</Button>
                        )}
                        {isAdmin && (
                            <Button variant="outlined" size="medium" startIcon={<AdminPanelSettingsIcon />} onClick={() => setView('moderation')}>Moderation</Button>
                        )}
                        <Button 
                            variant={showMyPosts ? "contained" : "outlined"} 
                            size="medium" 
                            startIcon={<PersonIcon />} 
                            onClick={() => setShowMyPosts(!showMyPosts)}
                        >
                            My Questions
                        </Button>
                        <Button variant="contained" size="medium" startIcon={<ForumIcon />} onClick={() => setView('create')}>Ask the Community</Button>
                    </Stack>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
                ) : (
                    <Box>
                        {posts.length === 0 ? (
                            <Paper sx={{ p: 6, textAlign: 'center', border: '1px solid #dadce0', boxShadow: 'none' }}>
                                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>No discussions found</Typography>
                                <Typography variant="body2" color="text.secondary">Be the first to ask a question!</Typography>
                            </Paper>
                        ) : (
                            posts.map(post => (
                                <PostCard key={post.id} onClick={() => fetchPostDetails(post.id)}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box sx={{ flex: 1, pr: 2 }}>
                                            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '1rem', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                                                {post.title}
                                                {post.is_approved === false && (
                                                    <Chip 
                                                        label="Pending Approval" 
                                                        size="small" 
                                                        color="warning"
                                                        sx={{ height: 20, fontSize: '0.65rem' }} 
                                                    />
                                                )}
                                            </Typography>
                                            <Typography 
                                                variant="body2" 
                                                color="text.secondary" 
                                                sx={{ 
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    lineHeight: 1.5
                                                }}
                                                dangerouslySetInnerHTML={renderFormattedText(post.content)}
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, minWidth: 'fit-content' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'block', fontWeight: 500, lineHeight: 1.3, color: 'text.primary' }}>
                                                        {post.user_name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', lineHeight: 1.3 }}>
                                                        {new Date(post.created_at).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                                <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: '#5f6368', fontWeight: 500 }}>
                                                    {post.user_name?.[0]?.toUpperCase()}
                                                </Avatar>
                                            </Box>
                                            <Chip 
                                                label={`${post.reply_count || 0} ${post.reply_count === 1 ? 'Reply' : 'Replies'}`} 
                                                size="small" 
                                                sx={{ bgcolor: '#e8f0fe', color: '#1967d2', fontWeight: 500, fontSize: '0.7rem', height: 22 }} 
                                            />
                                            {isAdmin && (
                                                <Box sx={{ mt: 1, display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                                                    <Tooltip title="Delete Post">
                                                        <IconButton size="small" color="error" onClick={() => handleAdminDeletePost(post.id)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </PostCard>
                            ))
                        )}
                    </Box>
                )}
            </Container>
        </Box>
    );

    const renderDetailView = () => {
        const isOwner = currentUser && currentPost && (currentPost.post.user_id === currentUser.id || currentPost.post.userId === currentUser.id);
        
        return (
            <Container maxWidth="lg" sx={{ mt: 1.5, mb: 4, px: { xs: 2, sm: 3 } }}>
                <StyledBackButton startIcon={<ArrowBackIcon />} onClick={() => setView('list')}>
                    Back to Community
                </StyledBackButton>
                {currentPost && !currentPost.post.is_approved && (
                    <Alert 
                        severity="warning" 
                        sx={{ mb: 2, borderRadius: 2 }}
                        icon={<PendingIcon />}
                    >
                        <Typography variant="body2" fontWeight="600">
                            This post is pending approval
                        </Typography>
                        <Typography variant="caption">
                            {isOwner 
                                ? "Your post is waiting for admin review. You can still edit it, but changes will also require approval."
                                : "This post is currently under review by administrators."}
                        </Typography>
                    </Alert>
                )}
                
                {currentPost && (
                    <>
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                p: { xs: 2.5, sm: 4 },
                                mb: 3,
                                border: '1px solid #e0e0e0',
                                borderRadius: 3,
                                bgcolor: 'background.paper',
                                transition: 'box-shadow 0.3s',
                                '&:hover': {
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                                }
                            }}
                        >
                            {isEditing ? (
                                <Box>
                                    <TextField 
                                        fullWidth 
                                        label="Title" 
                                        value={editTitle} 
                                        onChange={(e) => setEditTitle(e.target.value)} 
                                        sx={{ mb: 2 }} 
                                    />
                                    <RichTextEditor 
                                        value={editContent} 
                                        onChange={(e) => setEditContent(e.target.value)} 
                                        minRows={6} 
                                    />
                                    
                                    {isAdmin && (
                                        <Box sx={{ mt: 2 }}>
                                            <TextField
                                                fullWidth
                                                label="Custom Display Name (Admin Only)"
                                                placeholder="Enter custom name to display (optional)"
                                                variant="outlined"
                                                value={editDisplayName}
                                                onChange={(e) => setEditDisplayName(e.target.value)}
                                                helperText="Leave empty to keep current name"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <BadgeIcon color="primary" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Box>
                                    )}
                                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                        <Button variant="contained" onClick={handleUpdatePost} disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save Changes'}</Button>
                                        <Button variant="outlined" onClick={handleCancelEdit} disabled={isSubmitting}>Cancel</Button>
                                    </Box>
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Typography variant="h4" fontWeight="600" color="primary.main">
                                            {currentPost.post.title}
                                        </Typography>
                                        {isOwner && (
                                            <Box>
                                                <Tooltip title="Edit Post"><IconButton onClick={handleEditClick} size="small"><EditIcon /></IconButton></Tooltip>
                                                <Tooltip title="Delete Post"><IconButton onClick={handleDeletePost} size="small" color="error"><DeleteIcon /></IconButton></Tooltip>
                                            </Box>
                                        )}
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                                        <Avatar sx={{ width: 40, height: 40, bgcolor: currentPost.post.user_role === 'admin' ? theme.palette.warning.main : 'primary.main' }}>
                                            {currentPost.post.user_name?.[0]?.toUpperCase()}
                                        </Avatar>
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle1" fontWeight="600">{currentPost.post.user_name}</Typography>
                                                {currentPost.post.user_role === 'admin' && (
                                                    <Chip 
                                                        icon={<VerifiedIcon />} 
                                                        label="Product Expert" 
                                                        size="small" 
                                                        color="warning" 
                                                        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500 }} 
                                                    />
                                                )}
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                Posted on {new Date(currentPost.post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ my: 3 }} />

                                    <Box 
                                        className="post-content"
                                        sx={{ 
                                            lineHeight: 1.7, 
                                            color: 'text.primary', 
                                            fontSize: '1.05rem',
                                            '& img': { maxWidth: '100%', borderRadius: '8px', my: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
                                            '& p': { mb: 2 },
                                            '& a': { color: 'primary.main', textDecoration: 'underline' },
                                            '& strong': { color: 'text.primary', fontWeight: 600 },
                                            '& ul, & ol': { pl: 3 },
                                            '& li': { mb: 0.5 }
                                        }} 
                                    >
                                        <div dangerouslySetInnerHTML={renderFormattedText(currentPost.post.content)} />
                                    </Box>
                                    {currentPost.post.media && currentPost.post.media.length > 0 && (
                                        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #f0f0f0' }}>
                                            <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1.5 }}>Attachments</Typography>
                                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                                {currentPost.post.media.map((media, idx) => (
                                                    <Box key={idx} sx={{ width: 120, height: 120, borderRadius: 2, overflow: 'hidden', border: '1px solid #ddd', cursor: 'pointer', '&:hover img': { transform: 'scale(1.05)' } }}>
                                                        <img 
                                                            src={media.url.startsWith('http') ? media.url : `${API_BASE_URL}${media.url}`} 
                                                            alt="attachment" 
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.2s' }} 
                                                            onClick={() => window.open(media.url.startsWith('http') ? media.url : `${API_BASE_URL}${media.url}`, '_blank')} 
                                                        />
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Paper>

                        <Typography variant="h5" fontWeight="600" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.primary' }}>
                            <QuestionAnswerIcon />
                            {currentPost.replies.length} {currentPost.replies.length === 1 ? 'Reply' : 'Replies'}
                        </Typography>

                        <Stack spacing={2}>
                            {currentPost.replies.map(reply => (
                                <ReplyCard 
                                    key={reply.id} 
                                    elevation={0} 
                                    isadmin={reply.user_role === 'admin' ? 'true' : 'false'}
                                    ispending={reply.is_approved === false ? 'true' : 'false'}
                                    ishighlighted={reply.id === highlightReplyId ? 'true' : 'false'}
                                    ref={reply.id === highlightReplyId ? highlightedReplyRef : null}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: reply.user_role === 'admin' ? 'warning.main' : 'secondary.main' }}>
                                            {reply.user_name?.[0]?.toUpperCase()}
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography variant="subtitle2" fontWeight="600">
                                                    {reply.user_name}
                                                </Typography>
                                                {reply.user_role === 'admin' && (
                                                    <Chip label="Product Expert" size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 500 }} />
                                                )}
                                                {reply.is_approved === false && (
                                                    <Chip 
                                                        label="Pending Approval" 
                                                        size="small" 
                                                        color="warning"
                                                        icon={<PendingIcon />}
                                                        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 500 }} 
                                                    />
                                                )}
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                {new Date(reply.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                            </Typography>
                                        </Box>
                                        {isAdmin && (
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {reply.is_approved === false ? (
                                                    <>
                                                        <Tooltip title="Reject Reply">
                                                            <IconButton 
                                                                size="small" 
                                                                color="error"
                                                                onClick={() => setConfirmDialog({ open: true, id: reply.id, type: 'reply', action: 'reject' })}
                                                            >
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Approve Reply">
                                                            <IconButton 
                                                                size="small" 
                                                                color="success"
                                                                onClick={() => setConfirmDialog({ open: true, id: reply.id, type: 'reply', action: 'approve' })}
                                                            >
                                                                <CheckCircleIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                ) : null}
                                                <Tooltip title="Delete Reply">
                                                    <IconButton size="small" color="error" onClick={() => handleAdminDeleteReply(reply.id)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        )}
                                    </Box>
                                    <Box 
                                        className="reply-content"
                                        sx={{ 
                                            lineHeight: 1.7, 
                                            fontSize: '0.95rem', 
                                            color: 'text.secondary',
                                            '& img': { maxWidth: '100%', borderRadius: '8px', my: 1.5, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                                            pl: { xs: 0, sm: '48px' }
                                        }}
                                    >
                                        <div dangerouslySetInnerHTML={renderFormattedText(reply.content)} />
                                    </Box>

                                    {reply.media && reply.media.length > 0 && (
                                        <Box sx={{ mt: 2, pl: { xs: 0, sm: '48px' }, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {reply.media.map((media, idx) => (
                                                <Box key={idx} sx={{ width: 80, height: 80, borderRadius: 2, overflow: 'hidden', border: '1px solid #eee', cursor: 'pointer', '&:hover img': { transform: 'scale(1.05)' } }}>
                                                    <img 
                                                        src={media.url.startsWith('http') ? media.url : `${API_BASE_URL}${media.url}`} 
                                                        alt="attachment" 
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.2s' }} 
                                                        onClick={() => window.open(media.url.startsWith('http') ? media.url : `${API_BASE_URL}${media.url}`, '_blank')} 
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </ReplyCard>
                            ))}
                        </Stack>

                        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mt: 3, border: '1px solid #e0e0e0', borderRadius: 3, bgcolor: '#fafbfd' }}>
                            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 2 }}>
                                Your Reply
                            </Typography>
                            <RichTextEditor 
                                value={replyContent} 
                                onChange={(e) => setReplyContent(e.target.value)} 
                                placeholder="Write your reply here..." 
                                minRows={5} 
                                onAttach={(e) => handleFileSelect(e, setReplyImages, setReplyPreviews)}
                            />
                            {isAdmin && (
                                <Box sx={{ mt: 2 }}>
                                    <TextField
                                        fullWidth
                                        placeholder="Custom display name (Admin only - optional)"
                                        variant="outlined"
                                        value={replyDisplayName}
                                        onChange={(e) => setReplyDisplayName(e.target.value)}
                                        size="small"
                                        helperText="Leave empty to use your default name"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <BadgeIcon fontSize="small" color="primary" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            )}
                            {replyPreviews.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" fontWeight="500">Attachments:</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                        {replyPreviews.map((preview, index) => (
                                            <Box key={index} sx={{ position: 'relative', width: 80, height: 80 }}>
                                                <img src={preview.url} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleRemovePreview(index, setReplyImages, setReplyPreviews)}
                                                    sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white', boxShadow: 2, '&:hover': { bgcolor: '#f5f5f5' } }}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2.5 }}>
                                {!isAdmin && (
                                    <Typography variant="caption" color="text.secondary">
                                        Replies require admin approval before appearing.
                                    </Typography>
                                )}
                                <Button 
                                    variant="contained" 
                                    endIcon={isSubmitting ? null : <SendIcon />} 
                                    onClick={handleReply} 
                                    disabled={!replyContent.trim() || isSubmitting} 
                                    sx={{ ml: 'auto' }}
                                >
                                    {isSubmitting ? 'Posting...' : 'Post Reply'}
                                </Button>
                            </Box>
                        </Paper>
                    </>
                )}
            </Container>
        );
    };

    const renderCreateView = () => ( 
        <Container maxWidth="lg" sx={{ mt: 1, mb: 2, px: { xs: 1.5, sm: 2, md: 3 } }}> 
            <StyledBackButton startIcon={<ArrowBackIcon />} onClick={() => setView('list')}>
                Back
            </StyledBackButton>
            <Paper sx={{ p: 2.5, border: '1px solid #dadce0' }} elevation={0}> 
                <Typography variant="h5" fontWeight="bold" gutterBottom color="primary" sx={{ mb: 0.5 }}> 
                    Ask the Community 
                </Typography> 
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}> 
                    Share details about your question so the community can provide helpful answers 
                </Typography> 
                 
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}> 
                    Short summary of your issue 
                </Typography> 
                <TextField  
                    fullWidth  
                    placeholder="Example: How do I update my contact information?"  
                    variant="outlined"  
                    value={newPostTitle}  
                    onChange={(e) => setNewPostTitle(e.target.value)}  
                    sx={{ mb: 1.5 }}  
                /> 
                 
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}> 
                    Explain the problem you're facing and what you've tried 
                </Typography> 
                <RichTextEditor  
                    value={newPostContent}  
                    onChange={(e) => setNewPostContent(e.target.value)}  
                    placeholder="Provide details about your issue..."  
                    minRows={6}  
                    onAttach={(e) => handleFileSelect(e, setNewPostImages, setNewPostPreviews)}
                /> 
                {isAdmin && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BadgeIcon fontSize="small" color="primary" />
                            Custom Display Name (Admin Only)
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter custom name to display (optional)"
                            variant="outlined"
                            value={customDisplayName}
                            onChange={(e) => setCustomDisplayName(e.target.value)}
                            sx={{ mb: 0 }}
                            helperText="Leave empty to use your default name"
                        />
                    </Box>
                )}
                <Box sx={{ mt: 2 }}>
                    {newPostPreviews.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                            {newPostPreviews.map((preview, index) => (
                                <Box key={index} sx={{ position: 'relative', width: 100, height: 100 }}>
                                    <img src={preview.url} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                                    <IconButton 
                                        size="small" 
                                        onClick={() => handleRemovePreview(index, setNewPostImages, setNewPostPreviews)}
                                        sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: '#f5f5f5' } }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
                 
                <Button  
                    variant="contained"  
                    size="large"  
                    onClick={handleCreatePost}  
                    fullWidth  
                    sx={{ mt: 2 }}  
                    disabled={!newPostTitle.trim() || !newPostContent.trim() || isSubmitting} 
                > 
                    {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Post Question'}
                </Button> 
            </Paper> 
        </Container> 
    );

    const renderModerationView = () => (
        <Container maxWidth="lg" sx={{ mt: 1.5, mb: 4, px: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <StyledBackButton startIcon={<ArrowBackIcon />} onClick={() => setView('list')}>
                    Back to Forum
                </StyledBackButton>
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                        Moderation Queue
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        Review pending content from users
                    </Typography>
                </Box>
                <Box sx={{ width: 140 }} /> 
            </Box>
            
            {pendingPosts.length === 0 && pendingReplies.length === 0 ? (
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>No pending items to review</Alert>
            ) : (
                <Stack spacing={1.5}>
                    {pendingPosts.length > 0 && (
                        <>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>Pending Questions</Typography>
                            {pendingPosts.map(post => (
                                <Card key={post.id} variant="outlined" sx={{ border: '1px solid #dadce0', borderRadius: 1.5 }}>
                                    <CardContent sx={{ p: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="h6" fontSize="1rem" fontWeight="bold" color="primary">
                                                {post.title}
                                            </Typography>
                                            <Chip label={post.user_role} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} dangerouslySetInnerHTML={renderFormattedText(post.content)} />
                                        
                                        {post.media && post.media.length > 0 && (
                                            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                {post.media.map((media, idx) => (
                                                    <Box key={idx} sx={{ width: 60, height: 60, borderRadius: 1, overflow: 'hidden', border: '1px solid #eee' }}>
                                                <img 
                                                    src={media.url.startsWith('http') ? media.url : `${API_BASE_URL}${media.url}`} 
                                                    alt="attachment" 
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                                />
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Posted by {post.user_name} on {new Date(post.created_at).toLocaleDateString()}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button 
                                                    variant="outlined" color="error" size="small"
                                                    startIcon={<CancelIcon sx={{ fontSize: 16 }} />}
                                                    onClick={() => setConfirmDialog({ open: true, id: post.id, type: 'post', action: 'reject' })}
                                                >
                                                    Reject
                                                </Button>
                                                <Button 
                                                    variant="contained" color="success" size="small"
                                                    startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                                    onClick={() => setConfirmDialog({ open: true, id: post.id, type: 'post', action: 'approve' })}
                                                >
                                                    Approve
                                                </Button>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}
                            <Divider sx={{ my: 3 }} />
                        </>
                    )}
                    {pendingReplies.length > 0 && (
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1, mb: 1 }}>Pending Replies</Typography>
                    )}
                    {pendingReplies.length > 0 && (
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1, mb: 1 }}>Pending Replies</Typography>
                    )}
                    {pendingReplies.map(reply => (
                       <Card 
                            key={reply.id} 
                            variant="outlined" 
                            sx={{ 
                                border: '1px solid #dadce0', 
                                borderRadius: 1.5, 
                                boxShadow: 'none', 
                                cursor: 'pointer', 
                                transition: 'all 0.2s',
                                '&:hover': { 
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                    borderColor: '#11224d'
                                } 
                            }}
                            onClick={() => handleViewPendingReply(reply)}
                        >
                            <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                        Reply to: <strong style={{ color: '#11224d' }}>{reply.post_title}</strong>
                                    </Typography>
                                    <Chip 
                                        label={reply.user_role} 
                                        size="small" 
                                        sx={{ 
                                            height: 20, 
                                            fontSize: '0.7rem',
                                            bgcolor: reply.user_role === 'admin' ? '#fff3e0' : '#e3f2fd',
                                            color: reply.user_role === 'admin' ? '#e65100' : '#1565c0',
                                            fontWeight: 500
                                        }} 
                                    />
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar sx={{ width: 26, height: 26, fontSize: '0.75rem', bgcolor: '#5f6368', fontWeight: 500 }}>
                                            {reply.user_name?.[0]?.toUpperCase()}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" fontWeight="500" sx={{ fontSize: '0.85rem', lineHeight: 1.2, color: 'text.primary' }}>
                                                {reply.user_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                {new Date(reply.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                                        <Button 
                                            variant="outlined" 
                                            color="error" 
                                            size="small"
                                            startIcon={<CancelIcon sx={{ fontSize: 16 }} />} 
                                            onClick={() => setConfirmDialog({ open: true, id: reply.id, type: 'reply', action: 'reject' })}
                                            sx={{ minWidth: 85, py: 0.5, fontSize: '0.8rem' }}
                                        >
                                            Reject
                                        </Button>
                                        <Button 
                                            variant="contained" 
                                            color="success" 
                                            size="small"
                                            startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />} 
                                            onClick={() => setConfirmDialog({ open: true, id: reply.id, type: 'reply', action: 'approve' })}
                                            sx={{ minWidth: 85, py: 0.5, fontSize: '0.8rem' }}
                                        >
                                            Approve
                                        </Button>
                                    </Box>
                                </Box>

                                <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f8f9fa', border: '1px solid #e8eaed', borderRadius: 1 }}>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            fontSize: '0.85rem', 
                                            lineHeight: 1.6, 
                                            color: 'text.primary',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            '& img': { maxWidth: '100%', borderRadius: '6px', my: 0.5 }
                                        }}
                                        dangerouslySetInnerHTML={renderFormattedText(reply.content)}
                                    />
                                    {reply.media && reply.media.length > 0 && (
                                        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {reply.media.map((media, idx) => (
                                                <Box key={idx} sx={{ width: 80, height: 80, borderRadius: 1, overflow: 'hidden', border: '1px solid #eee' }}>
                                                    <img 
                                                        src={media.url.startsWith('http') ? media.url : `${API_BASE_URL}${media.url}`} 
                                                        alt="attachment" 
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }} 
                                                        onClick={() => window.open(media.url.startsWith('http') ? media.url : `${API_BASE_URL}${media.url}`, '_blank')} 
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Paper>
                                 <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button 
                                        size="small" 
                                        endIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                                        sx={{ fontSize: '0.75rem', color: 'text.secondary', textTransform: 'none' }}
                                    >
                                        View in Context
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            )}
        </Container>
    );
    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', pb: 4 }}>
                {view === 'list' && renderListView()}
                {view === 'detail' && renderDetailView()}
                {view === 'create' && renderCreateView()}
                {view === 'moderation' && renderModerationView()}

                {confirmDialog.open && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1300
                        }}
                        onClick={() => setConfirmDialog({ open: false, id: null, type: null, action: null })}
                    >
                        <Paper
                            sx={{ p: 3, maxWidth: 400, m: 2 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Typography variant="h6" gutterBottom>
                                {confirmDialog.title || (confirmDialog.action === 'approve' ? `Approve ${confirmDialog.type === 'post' ? 'Post' : 'Reply'}` : `Reject ${confirmDialog.type === 'post' ? 'Post' : 'Reply'}`)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {confirmDialog.message || `Are you sure you want to ${confirmDialog.action} this ${confirmDialog.type}?`}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button 
                                    onClick={() => setConfirmDialog({ open: false, id: null, type: null, action: null })}
                                    variant="outlined"
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    onClick={() => {
                                        if (confirmDialog.onConfirm) {
                                            confirmDialog.onConfirm();
                                        } else {
                                            handleModeration(confirmDialog.id, confirmDialog.type, confirmDialog.action);
                                        }
                                    }} 
                                    variant="contained"
                                    color="primary"
                                >
                                    Confirm
                                </Button>
                            </Box>
                        </Paper>
                    </Box>
                )}

                <Snackbar 
                    open={notification.open} 
                    autoHideDuration={4000} 
                    onClose={() => setNotification({ ...notification, open: false })} 
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert severity={notification.severity} sx={{ width: '100%' }}>
                        {notification.message}
                    </Alert>
                </Snackbar>
            </Box>
        </ThemeProvider>
    );
};

export default SupportForum;